import { SortDescriptor } from '@progress/kendo-data-query';
import { PagingParams } from './PagedResult';

/** The most rows one request may ask for — the backend's `PagingDefaults.MaxTake`, a 400 above it. */
export const MAX_PAGE_SIZE = 500;

/**
 * #648: paging/sorting state for a server-paged kendo-grid. Plain class, no
 * inheritance — components hold one instance, bind [skip]/[pageSize]/[sort]
 * to it, forward (pageChange)/(sortChange) into it, and send toParams() with
 * every fetch.
 *
 * Only fields present in the sortFieldMap are server-sortable; the templates
 * mark all other columns [sortable]="false". An unmapped or cleared sort is
 * omitted from the request, so the endpoint's documented default order applies.
 */
export class PagedListState {
  public skip = 0;
  public pageSize = 50; // overwritten from UserSettingsService before the first load
  public sort: SortDescriptor[];

  constructor(
    private readonly sortFieldMap: Record<string, string>,
    defaultSort?: SortDescriptor[],
  ) {
    this.sort = defaultSort ?? [];
  }

  public toParams(): PagingParams {
    const params: PagingParams = { skip: this.skip, take: this.pageSize };
    const active = this.sort?.find((descriptor) => descriptor.dir);
    const backendField = active ? this.sortFieldMap[active.field] : undefined;
    if (backendField) {
      params.sortBy = backendField;
      params.sortDescending = active.dir === 'desc';
    }
    return params;
  }

  /**
   * `take` is honoured, capped at the backend's maximum (#199): a PDF export with
   * `[allPages]="true"` asks the grid for skip 0 / take total and restores the page afterwards.
   * Ignoring `take` made every such export contain the first page's rows and nothing else.
   */
  public onPageChange(event: { skip: number; take: number }): void {
    this.skip = event.skip;
    if (event.take > 0) {
      this.pageSize = Math.min(event.take, MAX_PAGE_SIZE);
    }
  }

  public onSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.skip = 0;
  }

  public resetPage(): void {
    this.skip = 0;
  }

  /**
   * #69: after a row is removed, the current page can sit past the end of the
   * now-shorter list — deleting the only row on page 2 leaves an empty grid with
   * the pager still on page 2. Steps back to the last page that holds data and
   * reports whether it moved, so the caller knows to fetch again.
   */
  public clampToTotal(total: number): boolean {
    if (this.skip === 0 || this.skip < total) {
      return false;
    }
    this.skip = total > 0 ? Math.floor((total - 1) / this.pageSize) * this.pageSize : 0;
    return true;
  }
}
